import { Check, CirclePlus, Pencil, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  useNotificationRules,
  useSaveNotificationRule,
  useDeleteNotificationRule,
} from '../hooks/useNotifications.js';
import type {
  SlackChannel,
  NotificationRule,
  NotificationRuleInput,
} from '../types/notification.js';

/** @graph-connects none */
const STATUS_OPTIONS = [
  { value: 'success' as const, labelKey: 'notification.status.success' },
  { value: 'warning' as const, labelKey: 'notification.status.warning' },
  { value: 'error' as const, labelKey: 'notification.status.error' },
];

/** @graph-connects none */
function emptyRuleInput(): NotificationRuleInput {
  return {
    notificationType: '',
    displayName: '',
    channelId: '',
    enabled: true,
    defaultStatus: 'success',
  };
}

/** @graph-connects none */
function ruleToInput(rule: NotificationRule): NotificationRuleInput {
  return {
    notificationType: rule.notificationType,
    displayName: rule.displayName,
    channelId: rule.channelId,
    enabled: rule.enabled,
    defaultStatus: rule.defaultStatus,
  };
}

/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] p-2';
/** @graph-connects none */
const INPUT =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ルール1行の表示・インライン編集・削除。
 * @graph-connects useSaveNotificationRule [calls] ルール更新
 * @graph-connects useDeleteNotificationRule [calls] ルール削除
 */
function RuleRow({
  rule,
  channels,
  canEdit,
}: {
  rule: NotificationRule;
  channels: SlackChannel[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<NotificationRuleInput>(ruleToInput(rule));
  const saveMutation = useSaveNotificationRule();
  const deleteMutation = useDeleteNotificationRule();
  const channelName =
    channels.find((ch) => ch.channelId === rule.channelId)?.channelName ?? rule.channelId;
  const defaultStatusLabelKey = STATUS_OPTIONS.find(
    (o) => o.value === rule.defaultStatus,
  )?.labelKey;

  if (editing) {
    return (
      <tr className="text-[var(--color-text-primary)]">
        <td className={TD}>
          <span className="text-xs font-mono">{form.notificationType}</span>
        </td>
        <td className={TD}>
          <input
            className={INPUT}
            value={form.displayName}
            onChange={(e) => {
              setForm({ ...form, displayName: e.target.value });
            }}
          />
        </td>
        <td className={TD}>
          <select
            className={INPUT}
            value={form.channelId}
            onChange={(e) => {
              setForm({ ...form, channelId: e.target.value });
            }}
          >
            <option value="">{t('notification.selectPlaceholder')}</option>
            {channels.map((ch) => (
              <option key={ch.channelId} value={ch.channelId}>
                {ch.channelName}
              </option>
            ))}
          </select>
        </td>
        <td className={`${TD} text-center`}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => {
              setForm({ ...form, enabled: e.target.checked });
            }}
          />
        </td>
        <td className={TD}>
          <select
            className={INPUT}
            value={form.defaultStatus}
            onChange={(e) => {
              setForm({
                ...form,
                defaultStatus: e.target.value as 'success' | 'warning' | 'error',
              });
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </td>
        <td className={TD}>
          <div className="flex gap-1">
            <button
              type="button"
              className="rounded p-1 text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-elevated)]"
              disabled={saveMutation.isPending}
              onClick={() => {
                saveMutation.mutate(
                  { isNew: false, input: form },
                  {
                    onSuccess: () => {
                      setEditing(false);
                    },
                  },
                );
              }}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
              onClick={() => {
                setEditing(false);
                setForm(ruleToInput(rule));
              }}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="text-[var(--color-text-primary)]">
      <td className={`${TD} text-xs font-mono`}>{rule.notificationType}</td>
      <td className={`${TD} text-xs`}>{rule.displayName}</td>
      <td className={`${TD} text-xs`}>{channelName}</td>
      <td className={`${TD} text-center text-xs`}>
        {rule.enabled ? (
          <span className="text-[var(--color-accent-primary)]">{t('notification.enabled')}</span>
        ) : (
          <span className="text-[var(--color-text-muted)]">{t('notification.disabled')}</span>
        )}
      </td>
      <td className={`${TD} text-xs`}>
        {defaultStatusLabelKey ? t(defaultStatusLabelKey) : rule.defaultStatus}
      </td>
      <td className={TD}>
        {canEdit ? (
          <div className="flex gap-1">
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
              onClick={() => {
                setEditing(true);
              }}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-error)]"
              disabled={deleteMutation.isPending}
              onClick={() => {
                deleteMutation.mutate(rule.notificationType);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 通知ルール一覧＋新規追加テーブル。
 * @graph-connects useNotificationRules [calls] ルール一覧取得
 * @graph-connects useSaveNotificationRule [calls] ルール作成
 * @graph-connects RuleRow [calls] ルール行表示
 */
export function RuleSection({ channels, canEdit }: { channels: SlackChannel[]; canEdit: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useNotificationRules();
  const saveMutation = useSaveNotificationRule();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NotificationRuleInput>(emptyRuleInput());

  if (isLoading)
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner" />
        </div>
      </Card>
    );
  if (error)
    return (
      <Card className="p-6 text-sm text-[var(--color-error)]">
        {t('notification.rule.fetchError')}
      </Card>
    );

  const rules = data?.rules ?? [];

  return (
    <Card className="overflow-x-auto p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('notification.rule.title')}
        </h2>
        {canEdit && !adding ? (
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
            onClick={() => {
              setAdding(true);
            }}
          >
            <CirclePlus className="h-3.5 w-3.5" aria-hidden="true" />
            {t('common.add')}
          </button>
        ) : null}
      </div>
      <table className="mt-3 w-full border-collapse text-xs">
        <thead>
          <tr className="text-[var(--color-text-secondary)]">
            <th className={`${TD} text-left`}>{t('notification.rule.typeHeader')}</th>
            <th className={`${TD} text-left`}>{t('notification.rule.displayNameHeader')}</th>
            <th className={`${TD} text-left`}>{t('notification.rule.channelHeader')}</th>
            <th className={`${TD} text-center`}>{t('notification.stateHeader')}</th>
            <th className={`${TD} text-left`}>{t('notification.rule.defaultStatusHeader')}</th>
            <th className={`${TD} text-left w-20`} />
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <RuleRow
              key={rule.notificationType}
              rule={rule}
              channels={channels}
              canEdit={canEdit}
            />
          ))}
          {adding ? (
            <tr className="text-[var(--color-text-primary)]">
              <td className={TD}>
                <input
                  className={`${INPUT} font-mono`}
                  placeholder="release-history"
                  value={form.notificationType}
                  onChange={(e) => {
                    setForm({ ...form, notificationType: e.target.value });
                  }}
                />
              </td>
              <td className={TD}>
                <input
                  className={INPUT}
                  placeholder={t('notification.rule.displayNamePlaceholder')}
                  value={form.displayName}
                  onChange={(e) => {
                    setForm({ ...form, displayName: e.target.value });
                  }}
                />
              </td>
              <td className={TD}>
                <select
                  className={INPUT}
                  value={form.channelId}
                  onChange={(e) => {
                    setForm({ ...form, channelId: e.target.value });
                  }}
                >
                  <option value="">{t('notification.selectPlaceholder')}</option>
                  {channels.map((ch) => (
                    <option key={ch.channelId} value={ch.channelId}>
                      {ch.channelName}
                    </option>
                  ))}
                </select>
              </td>
              <td className={`${TD} text-center`}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => {
                    setForm({ ...form, enabled: e.target.checked });
                  }}
                />
              </td>
              <td className={TD}>
                <select
                  className={INPUT}
                  value={form.defaultStatus}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      defaultStatus: e.target.value as 'success' | 'warning' | 'error',
                    });
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </td>
              <td className={TD}>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="rounded p-1 text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-elevated)]"
                    disabled={
                      saveMutation.isPending ||
                      !form.notificationType ||
                      !form.displayName ||
                      !form.channelId
                    }
                    onClick={() => {
                      saveMutation.mutate(
                        { isNew: true, input: form },
                        {
                          onSuccess: () => {
                            setAdding(false);
                            setForm(emptyRuleInput());
                          },
                        },
                      );
                    }}
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
                    onClick={() => {
                      setAdding(false);
                      setForm(emptyRuleInput());
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ) : null}
          {rules.length === 0 && !adding ? (
            <tr>
              <td colSpan={6} className={`${TD} p-4 text-center text-[var(--color-text-muted)]`}>
                {t('notification.rule.noRules')}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </Card>
  );
}
